import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/http';
import { useToasts } from '../hooks/useToasts';

function Feedback() {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToasts();

  const [content, setContent] = useState('');
  const [contact, setContact] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [imageFiles, setImageFiles] = useState([]); // File[]

  const pagePath = useMemo(() => {
    try {
      return window.location?.pathname || '';
    } catch (e) {
      return '';
    }
  }, []);

  const pageUrl = useMemo(() => {
    try {
      return window.location?.href || '';
    } catch (e) {
      return '';
    }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const message = String(content || '').trim();
    const contactInfo = String(contact || '').trim();

    if (!message) {
      showError('请填写反馈内容');
      return;
    }

    setSubmitting(true);
    try {
      let images = [];
      if (imageFiles.length) {
        const form = new FormData();
        imageFiles.slice(0, 6).forEach((f) => form.append('file', f));
        const uploadRes = await api.post('/feedback/attachments', form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const uploaded = Array.isArray(uploadRes.data?.files) ? uploadRes.data.files : [];
        images = uploaded
          .map((it) => {
            const objectName = it?.objectName ? String(it.objectName) : '';
            if (!objectName) return null;
            const fileName = it?.fileName ? String(it.fileName) : null;
            return fileName ? { objectName, fileName } : { objectName };
          })
          .filter(Boolean)
          .slice(0, 6);
      }

      await api.post('/feedback', {
        content: message,
        contact: contactInfo || null,
        pagePath: pagePath || null,
        pageUrl: pageUrl || null,
        images: images.length ? images : undefined,
      });
      showSuccess('提交成功，感谢你的反馈！');
      setContent('');
      setContact('');
      setImageFiles([]);
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('feedback submit failed:', err);
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.response?.data?.error || (err?.message ? String(err.message) : '提交失败，请稍后再试');

      // 对图片上传常见错误给出更明确提示
      if (status === 413) {
        showError('图片太大，上传失败（请压缩图片或换小一点的图片再试）');
      } else if (status === 501) {
        showError('图片上传服务不可用（MinIO 未配置或后端未更新/未重启）');
      } else if (String(msg || '').includes('404')) {
        showError('当前服务器未找到图片上传接口（可能后端未更新/未重启）');
      } else {
        showError(String(msg || '提交失败，请稍后再试'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSelectImages = (e) => {
    const files = Array.from(e?.target?.files || []).filter(Boolean);
    if (!files.length) return;

    const next = [...imageFiles, ...files].slice(0, 6);
    setImageFiles(next);

    // reset input so selecting the same file again will trigger onChange
    try {
      e.target.value = '';
    } catch (err) {
      // ignore
    }
  };

  const removeImageAt = (idx) => {
    setImageFiles((arr) => arr.filter((_, i) => i !== idx));
  };

  return (
    <div style={{ maxWidth: 520, margin: '80px auto', padding: 24, border: '1px solid #eee', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <h2 style={{ textAlign: 'center', marginBottom: 8 }}>意见反馈</h2>
      <div style={{ textAlign: 'center', color: '#666', fontSize: 13, marginBottom: 18 }}>
        匿名提交（不要求登录）。可选留联系方式便于跟进。
      </div>

      <form onSubmit={onSubmit}>
        <div style={{ marginBottom: 12 }}>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="请输入你的建议/问题/想法..."
            rows={7}
            style={{ width: '100%', padding: 10, boxSizing: 'border-box', borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
            required
          />
        </div>

        <div style={{ marginBottom: 18 }}>
          <input
            type="text"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="（可选）联系方式：电话/微信/邮箱"
            style={{ width: '100%', padding: 10, boxSizing: 'border-box', borderRadius: 6, border: '1px solid #ccc' }}
          />
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>图片（可选，最多 6 张）</div>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={onSelectImages}
            disabled={submitting || imageFiles.length >= 6}
            style={{ width: '100%' }}
          />
          {!!imageFiles.length && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {imageFiles.map((f, idx) => {
                let preview = '';
                try {
                  preview = URL.createObjectURL(f);
                } catch (e) {
                  preview = '';
                }
                return (
                  <div key={`${f.name}-${idx}`} style={{ width: 92 }}>
                    <div style={{ width: 92, height: 92, border: '1px solid #eee', borderRadius: 6, overflow: 'hidden', background: '#fafafa' }}>
                      {preview ? (
                        <img
                          alt={f.name}
                          src={preview}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onLoad={() => {
                            try { URL.revokeObjectURL(preview); } catch (e) { /* ignore */ }
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: 12, color: '#999', padding: 8 }}>预览失败</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeImageAt(idx)}
                      disabled={submitting}
                      style={{
                        marginTop: 6,
                        width: '100%',
                        padding: '4px 6px',
                        borderRadius: 6,
                        border: '1px solid #ddd',
                        background: '#fff',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        fontSize: 12,
                      }}
                    >移除</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={submitting}
          style={{
            width: '100%',
            padding: '12px',
            border: 'none',
            borderRadius: 6,
            background: submitting ? '#91caff' : '#1677ff',
            color: '#fff',
            fontSize: 15,
            cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? '提交中...' : '匿名提交'}
        </button>
      </form>

      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <button
          type="button"
          onClick={() => navigate('/login')}
          style={{ border: 'none', background: 'transparent', color: '#1677ff', cursor: 'pointer', padding: 0, fontSize: 13 }}
        >
          返回登录
        </button>
      </div>
    </div>
  );
}

export default Feedback;
